import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

import { plannerNotesService, type RunningNote } from '@/services/plannerNotesService';
import { PLANNER_QUERY_KEY } from '@/hooks/usePlanner';

const noteKey = (tenantId?: string | null) =>
  [...PLANNER_QUERY_KEY, 'running-note', tenantId] as const;

/** One running note per client: the same on every billing period. */
export function useRunningNote(tenantId?: string | null) {
  return useQuery({
    queryKey: noteKey(tenantId),
    queryFn: () => {
      if (!tenantId) throw new Error('Tenant is required');
      return plannerNotesService.get(tenantId);
    },
    enabled: Boolean(tenantId),
    staleTime: 15_000,
  });
}

export function useSaveRunningNote(tenantId?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (note: string) => {
      if (!tenantId) throw new Error('Tenant is required');
      return plannerNotesService.save(tenantId, note);
    },
    onSuccess: (saved) => {
      // Never log or echo the text; just put the saved value in the cache.
      qc.setQueryData<RunningNote>(noteKey(tenantId), {
        note: saved.note ?? '',
        updated_at: saved.updated_at ?? null,
        available: true,
      });
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : 'Could not save the notes');
    },
  });
}
